import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArtworkOrderCard } from "./ArtworkOrderCard";
import { PrivacySafeShareCard } from "./PrivacySafeShareCard";
import { useArtworkOrders } from "@/hooks/useArtworkOrders";
import type { CustomerArtworkOrder } from "@/hooks/useArtworkOrders";
import { Search, Filter, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GENERATED_IMAGE_RETENTION_DAYS, PAID_ORDER_SUPPORT_RETENTION_DAYS } from "@/constants/retentionPolicy";
import { getStyleById } from "@/constants/artStyles";

export const GenerationHistory = () => {
  const { orders, loading, fetchOrders, getIncompleteOrders, getCompletedOrders } = useArtworkOrders();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStyle, setFilterStyle] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [selectedOrder, setSelectedOrder] = useState<CustomerArtworkOrder | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleResume = (order: CustomerArtworkOrder) => {
    // Redirect to creation flow with pre-filled data
    const searchParams = new URLSearchParams({
      resume: 'true',
      orderId: order.id,
      style: order.selected_style,
      hasImage: order.original_image_url ? 'true' : 'false',
      hasGenerated: order.generated_image_url ? 'true' : 'false'
    });
    
    navigate(`/create/artwork?${searchParams.toString()}`);
  };

  const handleReorder = (order: CustomerArtworkOrder) => {
    if (!order.generated_image_url) {
      toast({
        title: "Image non disponible",
        description: `Cette création a peut-être expiré si elle n'a pas été commandée. Les créations non commandées restent disponibles pendant ${GENERATED_IMAGE_RETENTION_DAYS} jours.`,
        variant: "destructive",
      });
      return;
    }

    // Create a new order based on existing one
    const searchParams = new URLSearchParams({
      reorder: 'true',
      style: order.selected_style,
      generatedImage: order.generated_image_url
    });

    navigate(`/create/artwork?${searchParams.toString()}`);
  };

  const handleViewDetails = (order: CustomerArtworkOrder) => {
    setSelectedOrder(order);
  };

  const formatPrice = (priceCents: number) => {
    return (priceCents / 100).toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
    });
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(dateString));
  };

  const formatOptionalValue = (value: string | null) => value || "Non renseigné";

  const statusLabels: Record<string, string> = {
    pending: "En attente",
    processing: "Traitement",
    shipped: "Expédiée",
    delivered: "Livrée",
    cancelled: "Annulée",
    completed: "Terminée",
    generation_in_progress: "Génération en cours",
  };

  const paymentStatusLabels: Record<string, string> = {
    paid: "Payé",
    pending: "Paiement en attente",
    cancelled: "Annulé",
  };

  const filterOrders = (ordersList: CustomerArtworkOrder[]) => {
    let filtered = [...ordersList];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(order => 
        order.selected_style.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.format_size.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by style
    if (filterStyle !== "all") {
      filtered = filtered.filter(order => order.selected_style === filterStyle);
    }

    // Sort orders
    switch (sortBy) {
      case "date":
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case "price":
        filtered.sort((a, b) => b.price_cents - a.price_cents);
        break;
      case "style":
        filtered.sort((a, b) => a.selected_style.localeCompare(b.selected_style));
        break;
    }

    return filtered;
  };

  const incompleteOrders = filterOrders(getIncompleteOrders());
  const completedOrders = filterOrders(getCompletedOrders());

  const styles = [...new Set(orders.map(order => order.selected_style))];

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-muted h-32 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Mes Générations</h2>
          <p className="text-muted-foreground">
            Gérez vos créations et commandes d'œuvres d'art
          </p>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            Les créations non commandées restent disponibles pendant {GENERATED_IMAGE_RETENTION_DAYS} jours. Les images de commandes payées sont conservées jusqu'à {PAID_ORDER_SUPPORT_RETENTION_DAYS} jours pour le suivi et le SAV.
          </p>
        </div>
        <Button onClick={fetchOrders} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par style, format..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStyle} onValueChange={setFilterStyle}>
          <SelectTrigger className="w-[200px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrer par style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les styles</SelectItem>
            {styles.map(style => (
              <SelectItem key={style} value={style}>
                {getStyleById(style)?.name || style}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Date de création</SelectItem>
            <SelectItem value="price">Prix</SelectItem>
            <SelectItem value="style">Style artistique</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all">
            Toutes ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="incomplete">
            En cours ({incompleteOrders.length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Terminées ({completedOrders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {filterOrders(orders).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                {orders.length === 0 ? "Aucune génération trouvée" : "Aucun résultat pour ces filtres"}
              </p>
              {orders.length === 0 && (
                <Button onClick={() => navigate("/create")}>
                  Créer ma première œuvre
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filterOrders(orders).map((order) => (
                <ArtworkOrderCard
                  key={order.id}
                  order={order}
                  onResume={handleResume}
                  onReorder={handleReorder}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="incomplete" className="space-y-4">
          {incompleteOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Aucune génération en cours</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {incompleteOrders.map((order) => (
                <ArtworkOrderCard
                  key={order.id}
                  order={order}
                  onResume={handleResume}
                  onReorder={handleReorder}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Aucune génération terminée</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {completedOrders.map((order) => (
                <ArtworkOrderCard
                  key={order.id}
                  order={order}
                  onResume={handleResume}
                  onReorder={handleReorder}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="sm:max-w-2xl">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle>Détails de la génération</DialogTitle>
                <DialogDescription>
                  Créée le {formatDate(selectedOrder.created_at)} · mise à jour le {formatDate(selectedOrder.updated_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-6 md:grid-cols-[220px,1fr]">
                <div className="overflow-hidden rounded-lg border bg-muted">
                  {selectedOrder.generated_image_url || selectedOrder.original_image_url ? (
                    <img
                      src={selectedOrder.generated_image_url || selectedOrder.original_image_url}
                      alt={selectedOrder.generated_image_url ? "Œuvre générée" : "Image originale"}
                      className="aspect-square w-full object-cover"
                    />
                  ) : (
                    <div className="flex aspect-square items-center justify-center text-sm text-muted-foreground">
                      Aucune image disponible
                    </div>
                  )}
                </div>

                <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">Statut</dt>
                    <dd className="font-medium">{statusLabels[selectedOrder.status] || selectedOrder.status}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Paiement</dt>
                    <dd className="font-medium">
                      {selectedOrder.payment_status
                        ? paymentStatusLabels[selectedOrder.payment_status] || selectedOrder.payment_status
                        : "Non renseigné"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Style</dt>
                    <dd className="font-medium">{getStyleById(selectedOrder.selected_style)?.name || selectedOrder.selected_style}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Format</dt>
                    <dd className="font-medium">{selectedOrder.format_size}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Prix</dt>
                    <dd className="font-medium">{formatPrice(selectedOrder.price_cents)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Encadrement</dt>
                    <dd className="font-medium">{formatOptionalValue(selectedOrder.frame_option)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Finition</dt>
                    <dd className="font-medium">{formatOptionalValue(selectedOrder.finish_type)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Support</dt>
                    <dd className="font-medium">{formatOptionalValue(selectedOrder.support_material)}</dd>
                  </div>

                  {selectedOrder.special_instructions && (
                    <div className="sm:col-span-2">
                      <dt className="text-muted-foreground">Instructions</dt>
                      <dd className="font-medium">{selectedOrder.special_instructions}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                {(selectedOrder.status === "completed" || selectedOrder.generated_image_url) && (
                  <div className="w-full sm:mr-auto sm:max-w-sm">
                    <PrivacySafeShareCard selectedStyle={selectedOrder.selected_style} variant="compact" />
                  </div>
                )}
                {(selectedOrder.status === "pending" ||
                  (selectedOrder.status === "generation_in_progress" && !selectedOrder.generated_image_url)) && (
                  <Button variant="outline" onClick={() => handleResume(selectedOrder)}>
                    Reprendre
                  </Button>
                )}
                {(selectedOrder.status === "completed" || selectedOrder.generated_image_url) && (
                  <Button onClick={() => handleReorder(selectedOrder)}>
                    Recommander
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};